import { forwardRef, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../ui';

/**
 * MasteryTimeline (spec §7.3, §9.3) — attempts over time next to the trend of
 * how right those attempts were, plus the FSRS stability of the underlying
 * memory when the mission is card-backed.
 *
 * Principle 4 of §3.3 ("Transfer statt Aktivität messen") is enforced in the
 * presentation: the bar series is explicitly labelled *activity*, the trend
 * line is the competence signal, and a standing note says the two are not the
 * same thing. A tall bar chart must never read as "mastered".
 *
 * Spec §12 requires a tabular alternative for every graph — it is rendered as
 * a real `<table>` in a `<details>`, not as a tooltip.
 */

const VIEW_W = 320;
const VIEW_H = 120;
const PAD_X = 6;
const PAD_Y = 8;

function toDayLabel(value, language) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(language, { day: '2-digit', month: 'short' });
}

/**
 * @param {object} props
 * @param {{day: string, attempts: number, successes: number, averageCorrectness: number|null}[]} props.points
 *   newest LAST (the mastery endpoint returns `GROUP BY day ORDER BY day`).
 * @param {number|null} [props.stabilityDays] current FSRS stability in days.
 * @param {string} [props.className]
 */
export const MasteryTimeline = forwardRef(function MasteryTimeline(
  { points = [], stabilityDays = null, className = '', ...rest },
  ref
) {
  const { t, i18n } = useTranslation();
  const tableId = useId();

  const series = useMemo(
    () =>
      (Array.isArray(points) ? points : []).map((point) => ({
        day: point.day,
        attempts: Number(point.attempts) || 0,
        successes: Number(point.successes) || 0,
        averageCorrectness:
          point.averageCorrectness === null || point.averageCorrectness === undefined
            ? null
            : Math.min(1, Math.max(0, Number(point.averageCorrectness))),
      })),
    [points]
  );

  if (series.length === 0) {
    return (
      <div ref={ref} className={`etch-mastery-timeline ${className}`.trim()} {...rest}>
        <EmptyState title={t('mastery.timelineEmptyTitle')}>{t('mastery.timelineEmptyBody')}</EmptyState>
      </div>
    );
  }

  const maxAttempts = Math.max(1, ...series.map((p) => p.attempts));
  const innerW = VIEW_W - PAD_X * 2;
  const innerH = VIEW_H - PAD_Y * 2;
  const slot = innerW / series.length;
  const barW = Math.max(2, Math.min(18, slot * 0.55));

  const trendPoints = series
    .map((point, index) => {
      if (point.averageCorrectness === null) return null;
      const x = PAD_X + slot * index + slot / 2;
      const y = PAD_Y + innerH - point.averageCorrectness * innerH;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .filter(Boolean);

  const totalAttempts = series.reduce((sum, p) => sum + p.attempts, 0);
  const totalSuccesses = series.reduce((sum, p) => sum + p.successes, 0);

  const chartLabel = t('mastery.timelineChartLabel', {
    attempts: totalAttempts,
    successes: totalSuccesses,
    days: series.length,
  });

  return (
    <div ref={ref} className={`etch-mastery-timeline ${className}`.trim()} {...rest}>
      <p className="etch-mastery-timeline__note">{t('mastery.activityNotCompetence')}</p>

      <figure className="etch-mastery-timeline__figure">
        <svg
          className="etch-mastery-timeline__chart"
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          role="img"
          aria-label={chartLabel}
          aria-describedby={tableId}
        >
          {/* Baseline and the 70% correctness pass mark — the threshold the
              mastery model actually uses (MASTERY_CONSTANTS.CORRECTNESS_PASS). */}
          <line
            className="etch-mastery-timeline__axis"
            x1={PAD_X}
            y1={PAD_Y + innerH}
            x2={VIEW_W - PAD_X}
            y2={PAD_Y + innerH}
          />
          <line
            className="etch-mastery-timeline__threshold"
            x1={PAD_X}
            y1={PAD_Y + innerH - 0.7 * innerH}
            x2={VIEW_W - PAD_X}
            y2={PAD_Y + innerH - 0.7 * innerH}
          />
          {series.map((point, index) => {
            const height = (point.attempts / maxAttempts) * innerH;
            const x = PAD_X + slot * index + (slot - barW) / 2;
            return (
              <rect
                key={point.day ?? index}
                className="etch-mastery-timeline__bar"
                x={x.toFixed(1)}
                y={(PAD_Y + innerH - height).toFixed(1)}
                width={barW.toFixed(1)}
                height={Math.max(1, height).toFixed(1)}
                rx="1.5"
              />
            );
          })}
          {trendPoints.length > 1 && (
            <polyline className="etch-mastery-timeline__trend" points={trendPoints.join(' ')} />
          )}
          {trendPoints.map((pair) => {
            const [cx, cy] = pair.split(',');
            return (
              <circle key={pair} className="etch-mastery-timeline__point" cx={cx} cy={cy} r="2.4" />
            );
          })}
        </svg>

        <figcaption className="etch-mastery-timeline__legend">
          <span className="etch-mastery-timeline__legend-item" data-series="attempts">
            {t('mastery.legendActivity')}
          </span>
          <span className="etch-mastery-timeline__legend-item" data-series="trend">
            {t('mastery.legendCorrectness')}
          </span>
          {stabilityDays !== null && stabilityDays !== undefined && (
            <span className="etch-mastery-timeline__legend-item" data-series="stability">
              {t('mastery.stabilityDays', { count: Math.round(stabilityDays) })}
            </span>
          )}
        </figcaption>
      </figure>

      <details className="etch-mastery-timeline__table">
        <summary>{t('mastery.tableAlternative')}</summary>
        <div className="etch-scroll-x">
          <table id={tableId}>
            <caption className="etch-visually-hidden">{chartLabel}</caption>
            <thead>
              <tr>
                <th scope="col">{t('mastery.colDay')}</th>
                <th scope="col">{t('mastery.colAttempts')}</th>
                <th scope="col">{t('mastery.colSuccesses')}</th>
                <th scope="col">{t('mastery.colAvgCorrectness')}</th>
              </tr>
            </thead>
            <tbody>
              {series.map((point, index) => (
                <tr key={point.day ?? index}>
                  <th scope="row">{toDayLabel(point.day, i18n.language)}</th>
                  <td>{point.attempts}</td>
                  <td>{point.successes}</td>
                  <td>
                    {point.averageCorrectness === null
                      ? '—'
                      : `${Math.round(point.averageCorrectness * 100)} %`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
});

export default MasteryTimeline;
